import React, { useRef, useState } from 'react';
import { Box, Button, Typography, Stack } from '@mui/material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import {
  CONTENT_BLOCK_HEIGHT,
  externalFlowContentColumnSx,
  externalFlowGlassCardLayoutSx,
  externalFlowInCardBrandingSx,
  externalFlowLineClamp3Sx,
  externalFlowPrimaryButtonSx,
  externalFlowSecondaryTextButtonSx,
  FLOW_MB_AFTER_SUBTITLE,
  LABEL_PRIMARY,
  LABEL_SECONDARY,
  LETTER_SPACING,
  SUBTITLE_FONT_SIZE,
  TITLE_FONT_SIZE,
  UPLOAD_FLOW_PAGE_BG,
} from './uploadCardLayout';

const UploadCard = ({ onFileSelect, validationError, onSeeWhatWorks }) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      onFileSelect(file);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) {
      onFileSelect(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const related = event.relatedTarget;
    if (!related || !event.currentTarget.contains(related)) {
      setIsDragging(false);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box
      component="main"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 0,
        flexShrink: 0,
        bgcolor: UPLOAD_FLOW_PAGE_BG,
        width: '100%',
      }}
    >
      <Box
        sx={{
          ...externalFlowContentColumnSx,
          minHeight: CONTENT_BLOCK_HEIGHT,
          maxHeight: CONTENT_BLOCK_HEIGHT,
        }}
      >
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          role="region"
          aria-label="Drop zone for floor plan upload"
          sx={{
            ...externalFlowGlassCardLayoutSx,
          }}
        >
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundImage: "url('/assets/floorplan-bg.png')",
              backgroundSize: 'cover',
              backgroundPosition: '65% 60%',
              transform: 'scale(1.08)',
              zIndex: 0,
              pointerEvents: 'none',
            }}
          />

          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(160deg, rgba(245,245,247,0.72) 0%, rgba(200,200,210,0.88) 100%)',
              zIndex: 1,
              pointerEvents: 'none',
            }}
          />

          {isDragging && (
            <Box
              aria-hidden="true"
              sx={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(0,0,0,0.18)',
                zIndex: 2,
                pointerEvents: 'none',
              }}
            />
          )}

          <Stack
            spacing={0}
            alignItems="center"
            sx={{ width: '100%', height: '100%', position: 'relative', zIndex: 3, pt: '8px' }}
          >
            <Box sx={{ flex: 1, minHeight: 0 }} />

            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: LABEL_PRIMARY,
                fontSize: TITLE_FONT_SIZE,
                letterSpacing: LETTER_SPACING,
                lineHeight: 1.2,
                textAlign: 'center',
                mb:
                  validationError &&
                  validationError !== "This file type isn't supported"
                    ? FLOW_MB_AFTER_SUBTITLE
                    : 0.5,
                ...externalFlowLineClamp3Sx,
              }}
            >
              {validationError || 'Add a floor plan'}
            </Typography>

            {(validationError === "This file type isn't supported" ||
              !validationError) && (
              <Typography
                variant="body1"
                sx={{
                  color: LABEL_SECONDARY,
                  fontSize: SUBTITLE_FONT_SIZE,
                  letterSpacing: LETTER_SPACING,
                  lineHeight: 1.4,
                  textAlign: 'center',
                  mb: '12px',
                  ...externalFlowLineClamp3Sx,
                }}
              >
                {validationError === "This file type isn't supported"
                  ? 'Upload a JPG or PNG'
                  : 'Get a furnished layout to shuffle and share'}
              </Typography>
            )}

            <Button
              variant="contained"
              size="medium"
              onClick={handleClick}
              aria-label="Upload floor plan"
              disableElevation
              startIcon={
                <FileUploadOutlinedIcon sx={{ fontSize: '20px !important' }} />
              }
              sx={{
                ...externalFlowPrimaryButtonSx,
                whiteSpace: 'nowrap',
                ...(isDragging && { background: 'rgba(255,255,255,0.55)' }),
              }}
            >
              Upload floor plan
            </Button>

            {onSeeWhatWorks && (
              <Button
                variant="text"
                size="small"
                onClick={onSeeWhatWorks}
                aria-label="Is my plan compatible?"
                sx={{
                  ...externalFlowSecondaryTextButtonSx,
                  // Extra vertical hit area; content above tightened to free room.
                  mt: 1.5,
                  py: 1.25,
                  minHeight: 40,
                }}
              >
                Is my plan compatible?
              </Button>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />

            <Box sx={{ flex: 1, minHeight: 0 }} />

            <Typography sx={externalFlowInCardBrandingSx}>
              Powered by MagicFurnish
            </Typography>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default UploadCard;
